import React from 'react';
import { Inbox, SearchX } from 'lucide-react';

export const FilterBar = ({ children, className = '', testId = 'filter-bar' }) => (
  <div
    data-testid={testId}
    className={`flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-3 shadow-xs ${className}`}
  >
    {children}
  </div>
);

export const FilterField = ({ label, children, className = '' }) => (
  <label className={`flex flex-col gap-1 ${className}`}>
    {label && <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>}
    {children}
  </label>
);

export const PresetGroup = ({ options, value, onChange, testIdPrefix = 'preset' }) => (
  <div className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-1" role="tablist">
    {options.map((o) => (
      <button
        key={o.key}
        type="button"
        role="tab"
        aria-selected={value === o.key}
        onClick={() => onChange(o.key)}
        data-testid={`${testIdPrefix}-${o.key}`}
        className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
          value === o.key ? 'bg-card text-primary shadow-xs' : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const PILL = {
  enrolled: 'bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold',
  renewed: 'bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold',
  paid: 'bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold',
  captured: 'bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold',
  completed: 'bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold',
  active: 'bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold',
  ok: 'bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold',
  wip: 'bg-sky-50 text-sky-800 border-sky-300',
  confirmed: 'bg-sky-50 text-sky-800 border-sky-300',
  consultation_scheduled: 'bg-indigo-50 text-indigo-800 border-indigo-300',
  consultation: 'bg-indigo-50 text-indigo-800 border-indigo-300',
  pending: 'bg-amber-50 text-amber-800 border-amber-300',
  trial_paid: 'bg-amber-50 text-amber-800 border-amber-300',
  trial: 'bg-amber-50 text-amber-800 border-amber-300',
  created: 'bg-amber-50 text-amber-800 border-amber-300',
  drift: 'bg-amber-100 text-amber-900 border-amber-300',
  no_show: 'bg-rose-50 text-rose-800 border-rose-300',
  failed: 'bg-rose-100 text-rose-800 border-rose-300',
  cancelled: 'bg-rose-50 text-rose-800 border-rose-300',
  not_interested: 'bg-slate-100 text-slate-700 border-slate-300',
  new: 'bg-slate-100 text-slate-700 border-slate-300',
  refunded: 'bg-purple-50 text-purple-800 border-purple-300',
};

export const StatusPill = ({ status, label, className = '', testId }) => {
  const key = String(status || '').toLowerCase().replace(/[\s-]+/g, '_');
  const text = label || String(status || '—').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span
      data-testid={testId || `status-pill-${key || 'none'}`}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${
        PILL[key] || 'bg-slate-100 text-slate-700 border-slate-300'
      } ${className}`}
    >
      {text}
    </span>
  );
};

export const EmptyState = ({ icon: Icon = Inbox, title, hint, action, variant = 'default', testId = 'empty-state' }) => {
  const Ico = variant === 'search' ? SearchX : Icon;
  return (
    <div data-testid={testId} className="rounded-xl border border-dashed border-border bg-card/60 p-10 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-slate-500">
        <Ico className="h-6 w-6" />
      </div>
      <p className="font-display text-base font-semibold text-foreground">{title}</p>
      {hint && <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{hint}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
};

export const Skeleton = ({ className = '' }) => <div className={`shimmer rounded-md ${className}`} />;

export const KpiCardSkeleton = () => (
  <div className="rounded-xl border border-border bg-card p-5 shadow-xs">
    <Skeleton className="h-3 w-24" />
    <Skeleton className="mt-3 h-8 w-32" />
    <Skeleton className="mt-3 h-3 w-20" />
  </div>
);

export const TableRowsSkeleton = ({ rows = 5, cols = 5 }) => (
  <div className="divide-y divide-border/60">
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="grid gap-4 px-4 py-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {Array.from({ length: cols }).map((__, c) => <Skeleton key={c} className="h-4 w-full" />)}
      </div>
    ))}
  </div>
);

export const ChartSkeleton = ({ height = 220 }) => <Skeleton className="w-full" style={{ height }} />;
